import { useEffect, useLayoutEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type RefObject } from "react";
import { activeElement, focusableIn } from "./util";

const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Keep keyboard focus inside `ref` while `active`. Moves focus in on activate
 * (to `[data-autofocus]`, else the first form field, else the container) and
 * restores it to whatever had focus before on deactivate. Returns a keydown
 * handler to attach to the container (wraps Tab / Shift+Tab).
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean) {
  useIsoLayoutEffect(() => {
    if (!active) return;
    const node = ref.current;
    if (!node) return;
    const root = node.getRootNode() as Document | ShadowRoot;
    const previous = activeElement(root) as HTMLElement | null;
    const target =
      node.querySelector<HTMLElement>("[data-autofocus]") ??
      node.querySelector<HTMLElement>("input:not([disabled]):not([type=hidden]):not([readonly]),textarea:not([disabled]),select:not([disabled])") ??
      node;
    // Defer so the element is laid out (animations, portals).
    const t = window.setTimeout(() => target.focus({ preventScroll: true }), 0);
    return () => {
      window.clearTimeout(t);
      if (previous && typeof previous.focus === "function" && previous.isConnected) {
        previous.focus({ preventScroll: true });
      }
    };
  }, [active]);

  return function onKeyDownTrap(e: ReactKeyboardEvent<HTMLElement>) {
    if (e.key !== "Tab") return;
    const node = ref.current;
    if (!node) return;
    const items = focusableIn(node);
    if (items.length === 0) {
      e.preventDefault();
      node.focus();
      return;
    }
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const current = activeElement(node.getRootNode() as Document | ShadowRoot);
    if (e.shiftKey && (current === first || current === node)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && current === last) {
      e.preventDefault();
      first.focus();
    }
  };
}

let scrollLocks = 0;
let savedOverflow = "";

/** Lock page scroll while a modal surface is open (ref-counted). */
export function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active || typeof document === "undefined") return;
    if (scrollLocks === 0) {
      savedOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    scrollLocks++;
    return () => {
      scrollLocks--;
      if (scrollLocks === 0) document.body.style.overflow = savedOverflow;
    };
  }, [active]);
}

/** Call `handler` on pointerdown outside every element in `refs`. */
export function useOutsidePointer(
  refs: Array<RefObject<HTMLElement | null>>,
  active: boolean,
  handler: () => void,
) {
  const saved = useRef(handler);
  saved.current = handler;
  useEffect(() => {
    if (!active) return;
    function onDown(e: PointerEvent) {
      const path = e.composedPath();
      for (const r of refs) {
        if (r.current && path.includes(r.current)) return;
      }
      saved.current();
    }
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}

export interface AnchorPosition {
  top: number;
  left: number;
  placement: "top" | "bottom";
}

/**
 * Position a floating element (fixed) next to an anchor; flips above when
 * there's no room below and keeps it inside the viewport. Re-runs on scroll
 * and resize while `active`.
 */
export function useAnchoredPosition(
  anchor: RefObject<HTMLElement | null>,
  floating: RefObject<HTMLElement | null>,
  active: boolean,
  opts: { align?: "start" | "end" | "center"; side?: "top" | "bottom"; offset?: number } = {},
  onChange?: (p: AnchorPosition) => void,
) {
  const { align = "start", side = "bottom", offset = 6 } = opts;
  const cb = useRef(onChange);
  cb.current = onChange;
  useIsoLayoutEffect(() => {
    if (!active) return;
    function place() {
      const a = anchor.current;
      const f = floating.current;
      if (!a || !f) return;
      const ar = a.getBoundingClientRect();
      const fw = f.offsetWidth;
      const fh = f.offsetHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let placement: "top" | "bottom" = side;
      if (side === "bottom" && ar.bottom + offset + fh > vh - 8 && ar.top - offset - fh > 8) placement = "top";
      if (side === "top" && ar.top - offset - fh < 8 && ar.bottom + offset + fh < vh - 8) placement = "bottom";
      const top = placement === "bottom" ? ar.bottom + offset : ar.top - offset - fh;
      let left = align === "start" ? ar.left : align === "end" ? ar.right - fw : ar.left + ar.width / 2 - fw / 2;
      left = Math.max(8, Math.min(left, vw - fw - 8));
      f.style.top = `${Math.round(top)}px`;
      f.style.left = `${Math.round(left)}px`;
      f.dataset.placement = placement;
      cb.current?.({ top, left, placement });
    }
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [active, align, side, offset]);
}
